import { PaletteOptions } from '@mui/material';

/**
 * Полная структура палетки (используется для дефолтов)
 */
export type BasePalette = typeof basePalettes.light;

/**
 * Конфиг повторяющий структуру палетки, но с опциональными значениями
 * для точечной кастомизации
 */
export type BrandThemeConfig = Partial<{
  primary: Partial<BasePalette['primary']>;
  secondary: Partial<BasePalette['secondary']>;
  background: Partial<BasePalette['background']>;
  text: Partial<BasePalette['text']>;
  divider: BasePalette['divider'];
  action: Partial<BasePalette['action']>;
  error: Partial<BasePalette['error']>;
  warning: Partial<BasePalette['warning']>;
  info: Partial<BasePalette['info']>;
  success: Partial<BasePalette['success']>;
}>;

const basePalettes = {
  light: {
    mode: 'light',
    primary: {
      main: '#50A2FF',
      light: '#82C3FF',
      dark: '#0073CE',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#DD722E',
      light: '#FF9B5C',
      dark: '#B25113',
      contrastText: '#ffffff',
    },
    background: {
      default: '#ffffff',
      paper: '#F7FAFC',
    },
    text: {
      primary: '#212121',
      secondary: '#424242',
      disabled: '#9e9e9e',
    },
    divider: '#e0e0e0',
    action: {
      active: '#50A2FF', //из light.primary.main
      hover: '#2E8FFF',
      selected: '#C0E0FF',
      disabled: '#c8ced3',
      disabledBackground: '#f0f0f0',
    },
    error: {
      main: '#B50003',
      light: '#FF6161',
      dark: '#7B0000',
    },
    warning: {
      main: '#FF991F',
      light: '#FFD580',
      dark: '#C27000',
    },
    info: {
      main: '#31708F',
      light: '#73A8C2',
      dark: '#1D4F6B',
    },
    success: {
      main: '#5ABD7B',
      light: '#8EE2A4',
      dark: '#2E8B57',
    },
  },

  dark: {
    mode: 'dark',
    primary: {
      main: '#50A2FF',
      light: '#80C3FF',
      dark: '#3d8bfd',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#FFB74D',
      light: '#FFD285',
      dark: '#C88719',
      contrastText: '#1E1E1E',
    },
    background: {
      default: '#1A202C',
      paper: '#2D3748',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#B0B0B0',
      disabled: '#6D6D6D',
    },
    divider: '#2C2C2C',
    action: {
      active: '#4CAEFF',
      hover: '#3e4c65',
      selected: '#1E2A33',
      disabled: '#555555',
      disabledBackground: '#2B2B2B',
    },
    error: {
      main: '#F44336',
      light: '#E57373',
      dark: '#B71C1C',
    },
    warning: {
      main: '#FFA726',
      light: '#FFCC80',
      dark: '#EF6C00',
    },
    info: {
      main: '#29B6F6',
      light: '#81D4FA',
      dark: '#0288D1',
    },
    success: {
      main: '#66BB6A',
      light: '#A5D6A7',
      dark: '#388E3C',
    },
  },
};

export const getPalette = (
  mode: 'light' | 'dark',
  config: BrandThemeConfig | null,
): PaletteOptions => {
  const base = basePalettes[mode];

  return {
    mode,
    primary: {
      main: config?.primary?.main || base.primary.main,
      light: config?.primary?.light || base.primary.light,
      dark: config?.primary?.dark || base.primary.dark,
      contrastText: config?.primary?.contrastText || base.primary.contrastText,
    },
    secondary: {
      main: config?.secondary?.main || base.secondary.main,
      light: config?.secondary?.light || base.secondary.light,
      dark: config?.secondary?.dark || base.secondary.dark,
      contrastText: config?.secondary?.contrastText || base.secondary.contrastText,
    },
    background: {
      default: config?.background?.default || base.background.default,
      paper: config?.background?.paper || base.background.paper,
    },
    text: {
      primary: config?.text?.primary || base.text.primary,
      secondary: config?.text?.secondary || base.text.secondary,
      disabled: config?.text?.disabled || base.text.disabled,
    },
    divider: config?.divider || base.divider,
    action: {
      active: config?.action?.active || base.action.active,
      hover: config?.action?.hover || base.action.hover,
      selected: config?.action?.selected || base.action.selected,
      disabled: config?.action?.disabled || base.action.disabled,
      disabledBackground: config?.action?.disabledBackground || base.action.disabledBackground,
    },
    error: {
      main: config?.error?.main || base.error.main,
      light: config?.error?.light || base.error.light,
      dark: config?.error?.dark || base.error.dark,
    },
    warning: {
      main: config?.warning?.main || base.warning.main,
      light: config?.warning?.light || base.warning.light,
      dark: config?.warning?.dark || base.warning.dark,
    },
    info: {
      main: config?.info?.main || base.info.main,
      light: config?.info?.light || base.info.light,
      dark: config?.info?.dark || base.info.dark,
    },
    success: {
      main: config?.success?.main || base.success.main,
      light: config?.success?.light || base.success.light,
      dark: config?.success?.dark || base.success.dark,
    },
  };
};
